"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import { useOpportunitiesStore } from "~/app/sales/opportunities/store";

import { SYSTEM_ATTRIBUTE_PROPERTY } from "~/core/constants/attributes";
import { routes } from "~/core/constants/routes";

import { useGlobalNewOpportunityModalContext } from "../context";
import { useGlobalCreateOpportunityStore } from "../store";
import { PipelineOption } from "../types";
import { checkFormHasValueWithoutPrePopulatedFields } from "../utils";

import { usePipelinesLastOpportunityCreatedQuery } from "./usePipelinesLastOpportunityCreatedQuery";
import { usePipelinesQuery } from "./usePipelinesQuery";

export const usePipelineSwitching = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { pipelines, isPipelinesLoading } = usePipelinesQuery();
  const { form } = useGlobalNewOpportunityModalContext();

  const setIsConfirmClearForm = useGlobalCreateOpportunityStore(
    state => state.setIsConfirmClearForm,
  );
  const resetStageSelection = useGlobalCreateOpportunityStore(
    state => state.resetStageSelection,
  );
  const {
    pipeline: lastOpportunityCreatedPipeline,
    isPipelineLoading: isLastOpportunityCreatedPipelineLoading,
  } = usePipelinesLastOpportunityCreatedQuery();

  const setPipelines = useGlobalCreateOpportunityStore(
    state => state.setPipelines,
  );
  const setIsPipelinesLoading = useGlobalCreateOpportunityStore(
    state => state.setIsPipelinesLoading,
  );
  const setSelectedPipeline = useGlobalCreateOpportunityStore(
    state => state.setSelectedPipeline,
  );

  const selectedPipeline = useGlobalCreateOpportunityStore(
    state => state.selectedPipeline,
  );
  const activePipeline = useOpportunitiesStore(state => state.activePipeline);

  useEffect(() => {
    setPipelines(
      (pipelines?.map(pipeline => ({
        label: pipeline.name,
        value: pipeline.id,
      })) as PipelineOption[]) || [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelines]);

  /**
   * Set the default selected pipeline to the:
   * 1. If the current page is an opportunities page and have the pipeline_id
   * 2. last opportunity created pipeline
   */
  useEffect(() => {
    const isOpportunitiesPage = pathname.startsWith(routes.opportunity.index);
    if (isOpportunitiesPage && !!activePipeline) {
      setSelectedPipeline({
        label: activePipeline.pipeline_name,
        value: activePipeline.pipeline_id,
      });
    } else if (lastOpportunityCreatedPipeline) {
      setSelectedPipeline(lastOpportunityCreatedPipeline);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePipeline, lastOpportunityCreatedPipeline, pathname, searchParams]);

  useEffect(() => {
    const hasValue = checkFormHasValueWithoutPrePopulatedFields(form);
    if (!!hasValue) {
      setIsConfirmClearForm(true);
    }
    resetStageSelection();
    form?.resetFields([SYSTEM_ATTRIBUTE_PROPERTY.OPPORTUNITY_STAGE]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPipeline]);

  useEffect(() => {
    setIsPipelinesLoading(
      isPipelinesLoading || isLastOpportunityCreatedPipelineLoading,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPipelinesLoading, isLastOpportunityCreatedPipelineLoading]);
};
